import { useCallback, useEffect, useState } from "react";
import { Lock, LockOpen, Unlock, ShieldAlert, Eye, EyeOff, Trash2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const FIELDS = [
  { k: "email", label: "Email", type: "email", ph: "you@example.com" },
  { k: "password", label: "Password", type: "password", ph: "••••••••" },
  { k: "phone", label: "Phone number", type: "tel", ph: "+1 555 000 0000" },
];

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function daysLeft(seconds) {
  return Math.ceil(seconds / 86400);
}

export default function EmergentLockCard() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", phone: "", notes: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [earlyConfirm, setEarlyConfirm] = useState(false);
  const earlyUnlock = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/admin/emergent-lock/early-unlock-once"); setState(data); setEarlyConfirm(false); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const load = useCallback(() => api.get("/admin/emergent-lock").then(({ data }) => setState(data)).catch(() => setState({ status: "empty" })), []);
  useEffect(() => { load(); }, [load]);

  const lock = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/emergent-lock", form);
      setState(data); setForm({ email: "", password: "", phone: "", notes: "" }); setConfirming(false);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.delete("/admin/emergent-lock"); setState(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!state) return null;

  return (
    <div className="mt-8 bg-ink text-cream rounded-2xl p-6" data-testid="emergent-lock-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold">
            <Lock className="w-3 h-3" strokeWidth={2} /> Emergent Lock info
          </div>
          <h2 className="font-serif text-2xl mt-1.5">
            {state.status === "locked" ? "Sealed." : state.status === "unlocked" ? "Unlocked — here's your note." : "Write it down, then seal it for a year."}
          </h2>
          <p className="text-xs text-sage-light mt-1 max-w-xl">
            Encrypted at rest and locked for {state.lock_days || 365} days. No early withdrawals — nothing can be read, edited, or deleted until the unlock date, not even with the admin PIN.
          </p>
        </div>
        {state.status !== "empty" && (
          <div className="text-right shrink-0" data-testid="emergent-lock-dates">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">{state.status === "locked" ? "Unlocks" : "Unlocked"}</div>
            <div className="font-mono text-lg text-white mt-0.5" data-testid="emergent-lock-unlock-date">{fmtDate(state.unlock_at)}</div>
            {state.status === "locked" && <div className="text-xs text-sage-light" data-testid="emergent-lock-days-left">{daysLeft(state.seconds_remaining)} days to go</div>}
          </div>
        )}
      </div>

      {state.status === "locked" && (
        <div className="mt-5 rounded-xl border border-ink-soft bg-white/5 p-5 text-sm text-sage-light" data-testid="emergent-lock-sealed">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-gold shrink-0" strokeWidth={1.5} />
            Locked on {fmtDate(state.locked_at)}. Contents hidden until {fmtDate(state.unlock_at)}.
          </div>
          {state.early_unlock_available && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              {!earlyConfirm ? (
                <button type="button" onClick={() => setEarlyConfirm(true)} className="inline-flex items-center gap-1.5 border border-gold text-gold rounded-lg px-3 py-2 text-xs font-medium hover:bg-gold hover:text-white" data-testid="emergent-lock-early-btn">
                  <Unlock className="w-3.5 h-3.5" /> Unlock early · one time only
                </button>
              ) : (
                <>
                  <span className="text-xs text-clay" data-testid="emergent-lock-early-confirm-text">This is your only early unlock, ever. Open it now to fix what's inside?</span>
                  <button type="button" onClick={earlyUnlock} disabled={busy} className="bg-clay text-white rounded-lg px-3 py-2 text-xs font-medium disabled:opacity-60" data-testid="emergent-lock-early-confirm-btn">Yes, unlock now</button>
                  <button type="button" onClick={() => setEarlyConfirm(false)} className="text-xs text-sage-light hover:text-white" data-testid="emergent-lock-early-cancel">Cancel</button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {state.status === "unlocked" && (
        <div className="mt-5 rounded-xl border border-ink-soft bg-white/5 p-5" data-testid="emergent-lock-contents">
          <dl className="grid sm:grid-cols-2 gap-4 text-sm">
            {FIELDS.map((f) => (
              <div key={f.k}>
                <dt className="text-[10px] uppercase tracking-[0.2em] text-sage">{f.label}</dt>
                <dd className="font-mono text-white mt-0.5 break-all" data-testid={`emergent-lock-value-${f.k}`}>{state.contents?.[f.k] || "—"}</dd>
              </div>
            ))}
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase tracking-[0.2em] text-sage">Notes</dt>
              <dd className="text-cream mt-0.5 whitespace-pre-wrap" data-testid="emergent-lock-value-notes">{state.contents?.notes || "—"}</dd>
            </div>
          </dl>
          <button onClick={clear} disabled={busy}
            className="mt-5 inline-flex items-center gap-1.5 text-xs text-clay hover:text-white transition-colors disabled:opacity-60"
            data-testid="emergent-lock-clear-btn">
            <Trash2 className="w-3.5 h-3.5" /> Clear and write a new note
          </button>
        </div>
      )}

      {state.status === "empty" && (
        <form onSubmit={(e) => { e.preventDefault(); setConfirming(true); }} className="mt-5 grid sm:grid-cols-2 gap-4" data-testid="emergent-lock-form">
          {FIELDS.map((f) => (
            <label key={f.k} className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-sage">{f.label}</span>
              <div className="relative mt-1.5">
                <input type={f.k === "password" && showPw ? "text" : f.type} value={form[f.k]} placeholder={f.ph} autoComplete="off"
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full bg-white/5 border border-ink-soft rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-sage-mid focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
                  data-testid={`emergent-lock-input-${f.k}`} />
                {f.k === "password" && (
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-sage hover:text-white" data-testid="emergent-lock-toggle-pw">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </label>
          ))}
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-sage">Notes · anything else</span>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Recovery codes, account IDs, reminders…"
              className="mt-1.5 w-full bg-white/5 border border-ink-soft rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-sage-mid focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
              data-testid="emergent-lock-input-notes" />
          </label>
          <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
            {!confirming ? (
              <button type="submit" className="inline-flex items-center gap-1.5 bg-gold text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gold-deep transition-colors" data-testid="emergent-lock-submit-btn">
                <Lock className="w-4 h-4" strokeWidth={2} /> Lock for 1 year
              </button>
            ) : (
              <>
                <span className="text-sm text-clay" data-testid="emergent-lock-confirm-text">Seal until {fmtDate(new Date(Date.now() + 365 * 86400000).toISOString())}? This cannot be undone.</span>
                <button type="button" onClick={lock} disabled={busy} className="inline-flex items-center gap-1.5 bg-clay text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="emergent-lock-confirm-btn">
                  <LockOpen className="w-4 h-4" strokeWidth={2} /> {busy ? "Sealing…" : "Yes, seal it"}
                </button>
                <button type="button" onClick={() => setConfirming(false)} className="text-sm text-sage-light hover:text-white" data-testid="emergent-lock-cancel-btn">Cancel</button>
              </>
            )}
          </div>
        </form>
      )}
      {err && <div className="mt-3 text-sm text-clay" data-testid="emergent-lock-error">{err}</div>}
    </div>
  );
}
